import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import TemplateTable from '../shared/tables/TemplateTable';
import { ITableHeader } from '../shared/tables/models/ITableHeader';
import { useNavigate, useParams } from 'react-router-dom';
import LoaderContainer from '../shared/loaderContainer/LoaderContainer';
import { setURLPaths } from '../../app/organizationReducer';
import { HTTP_STATUS } from '../../http/constants/http.status';
import { setToastData } from '../../app/toastReducer';
import { noDataAvailableMessage } from '../Organizations/helpers';
import { AppConstants } from '../../constants/AppConstants';
import { useQuery } from '../../hooks/queryParams';
import { RouteConstants } from '../../constants/RouteConstants';
import { getFailureMessage } from '../../util/ErrorUtil';
import { useTheme } from '../../context/themeContext';
import { useTranslation } from 'react-i18next';
import { listConsentTemplates } from '../../http/access-management-service';
import { setConsentTemplate, setDefaultValueSettings } from '../../app/consentReducer';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';
import BiButton from '../primitives/buttons/BiButton.primitive';
import ConsentListFilter, { filterConfigProps } from './consentListFilter';
import {
  getCodableValue,
  getPurpose,
  getCodableValueArray,
  getCodeFromCodable,
  GetObjectFromData
} from '../../util/consent-utils';
import { getSystemSettings } from '../../http/configuration-services';
import { SystemSetting } from '../../util/RoleUtil';
import AddCircle from '../../assets/addCircle.svg';
import moment from 'moment';
import { setTableOptions } from '../../app/templateTableReducer';
import { cloneDeep, findIndex, isEmpty } from 'lodash';
import { useDidMountEffect } from '../../util/customHooks';
import { ActionButtons } from '../Reusable/ActionButton';
import { deleteManifestItem, setManifest } from '../../app/migrationExportReducer';
import Checkbox from '../shared/Fields/Checkbox';
import { Group, Row } from '../shared/tables/TemplateTableStyles';
import {
  isGlobalSelected,
  toggleGlobalCheckbox,
  toggleGlobalOverrideCheckbox
} from '../Migration/migrationUtil';

const CustomersTableContainer = styled.div`
  padding: 1rem;
  width: 100%;
`;

const defaultSystemFilterOption = {
  purposeOption: new Map(),
  categoryOption: new Map(),
  statusOption: []
};

/**
 * @parentId consent-management
 * @manager ConsentTemplateList
 * @overview
 *   <section>
 *           <p>
 *               Consent template listing page is the default page the user is navigated on clicking <b>Consent Management</b> navigation bar menuitem. Listing page will show all the consent template(s) in a paginated way.
 *               The Default pagination size be fixed to 20 items, which can be changed to 50 or 100 from the dropdown menu of datagrid footer. Datagrid footer will also display the current page and total number of pages available based on the choosen page size & consent template data.
 *           </p>
 *           </br>
 *       <p>
 *           Admin user(s) can create new consent template by clicking on <b>New Consent</b> button provided on the admin portal toolbar,
 *           which will navigate user to Consent Tempplate creation page. <b>New Consent</b> button will be shown based on logged in
 *           user role API permissions as described below.
 *       </p>
 *           </br>
 *           <p>
 *               Consent Template(s) can be filtered based on the multiple attribute with filter button provided above the template listing data grid. We can filter the consent template list as per below attributes:
 *                <ul>
 *                    <li>Status</li>
 *                    <li>Consent Name</li>
 *                    <li>Type</li>
 *                    <li>Category</li>
 *                    <li>Purpose</li>
 *                    <li>Version</li>
 *                </ul>
 *           </p>
 *           </br>
 *           <p>
 *               Datagrid will display details of each consent template as row item with each column representing template property.
 *               <br>
 *               <ul>
 *                   <li>Name</li> <li>Category</li> <li>Purpose</li> <li>Type</li> <li>Version</li> <li>Status</li> <li>Action</li> <li>Last Modified On</li>
 *               </ul>
 *           </p>
 *           <p>
 *               Sorting of consent template grid is supported on <i>Status, Name, Type, Version, Last Modified On</i> columns. The breadcrumb bar on the application toolbar will reflect location of the user in the admin portal. Clicking on user rowitem will drill down user to user details page which will be displayed in readonly mode.
 *           </p>
 *         </section>
 *         <section>
 *          <h4>Failure Status Codes</h3>
 *           <p>
 *           This section describes the List Consent Template(s) Status Code information.
 *           <table>
 *               <tr>
 *                   <th>HTTP Status Code</th>
 *                   <th>Service Error Code</th>
 *                   <th>Error Message</th>
 *               </tr>
 *               <tr>
 *                   <td>403</td>
 *                   <td>UMS_PERMISSION_DENIED</td>
 *                   <td>You do not have permission to view this page</td>
 *               </tr>
 *               <tr>
 *                   <td>500</td>
 *                   <td>UMS_INTERNAL_ERROR</td>
 *                   <td>Internal Server Error</td>
 *               </tr>
 *               <tr>
 *                   <td>503</td>
 *                   <td></td>
 *                   <td>Service Unavailable</td>
 *               </tr>
 *           </table>
 *       </p>
 *       </section
 *       <section>
 *          <h4>Dependent System settings, Platform services & Role Permission</h3>
 *          <p>This section describes the list of dependent system settings & platform services required for functioning of Consent Template(s) listing page.</p>
 *           <h5>System Settings</h4>
 *           <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope</p>
 *           <table>
 *               <tr>
 *                   <th>Key</th>
 *                   <th>Type</th>
 *                   <th>Value</th>
 *                   <th>Scope</th>
 *               </tr>
 *               <tr>
 *                   <td>access.control.consent.template.type.default.values</td>
 *                   <td>String</td>
 *                   <td>PERMIT</td>
 *                   <td>None</td>
 *               </tr>
 *               <tr>
 *                   <td>access.control.consent.template.status.default.values</td>
 *                   <td>String</td>
 *                   <td>DRAFT</td>
 *                   <td>None</td>
 *               </tr>
 *               <tr>
 *                   <td>access.control.consent.template.action.default.codes</td>
 *                   <td>json</td>
 *                   <td>None</td>
 *                   <td>None</td>
 *               </tr>
 *               <tr>
 *                   <td>access.control.consent.template.category.default.values</td>
 *                   <td>json</td>
 *                   <td>None</td>
 *                   <td>None</td>
 *               </tr>
 *               <tr>
 *                   <td>access.control.consent.template.purpose.default.values</td>
 *                   <td>json</td>
 *                   <td>None</td>
 *                   <td>None</td>
 *               </tr>
 *           </table>
 *           <br>
 *          <h5>Platform Service(s)</h4>
 *          <p>Table lists all the dependent platform service(s) with specific version(s) for User(s) listing</p>
 *           <table>
 *               <tr>
 *                   <th>Service Name</th>
 *                   <th>Version</th>
 *               </tr>
 *               <tr>
 *                   <td>Access Control Service</td>
 *                   <td>1.5.0</td>
 *               </tr>
 *               <tr>
 *                   <td>Configuration Service</td>
 *                   <td>1.5.0</td>
 *               </tr>
 *           </table>
 *           <br>
 *          <h5>API Role Permission(s)</h4>
 *          <p>Table lists the required API role permissions for listing Consent Template(s) page</p>
 *           <table>
 *               <tr>
 *                   <th>API URL</th>
 *                   <th>API Method</th>
 *                   <th>API Permission</th>
 *                   <th>Required</th>
 *               </tr>
 *               <tr>
 *                  <td>/system-settings</td>
 *                  <td>GET</td>
 *                  <td>configuration-service.setting.list</td>
 *                  <td>Yes</td>
 *              </tr>
 *               <tr>
 *                   <td>/consent/templates</td>
 *                   <td>GET</td>
 *                   <td>N/A</td>
 *                   <td>Yes</td>
 *               </tr>
 *           </table>
 *       </section>
 *       <section>
 *        <p>Sequence Diagram for Consent Template(s) listing page</p>
 *       </section>
 */

interface ConsentTemplateProps {
  type?: 'listing' | 'migrationSelection' | 'migrationPreview';
  toggleModal?: Function;
}

const ConsentTemplateList = ({
  type = 'listing',
  toggleModal = () => {}
}: ConsentTemplateProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [pagingOpts, setPagingOpts] = useState<any>(null);
  const [rows, setRows] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [systemFilterOption, setSystemFilterOption] = useState<any>(defaultSystemFilterOption);
  const [selectedItems, setSelectedItems] = useState<any>([]);
  const [formFilterData, setFormFilterData] = useState<filterConfigProps>({
    status: '',
    type: '',
    name: '',
    version: '',
    'category-code': [],
    'purpose-code': []
  });
  const [currentActivePage, setCurrentActivePage] = useState(0);
  const [currentPage, setCurrentPage] = useState(AppConstants.DEFAULT_PAGE);

  const manifestConsentsData = useAppSelector(
    (state: any) => state.migrationExport?.manifest?.consentTemplates
  );
  const globalFilters: any = useAppSelector((state) => state.templateTableReducer.tableOptions);

  const consentTemplateSelector = (data: any, step: number) => {
    if (type === 'listing') {
      dispatch(setConsentTemplate(data));
      navigate(`/consents/${data.id}`);
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const defaultSystemSettings: Array<SystemSetting> =
        await getConsentTemplateDefaultSystemSettings();
      dispatch(setDefaultValueSettings(defaultSystemSettings));

      let statusOption: any = [];
      let purposeOption: Map<string, any> = new Map<string, any>();
      let categoryOption: Map<string, any> = new Map<string, any>();

      defaultSystemSettings.forEach((ele: any) => {
        if (ele.key === AppConstants.SYSTEM_CONFIG_CONSENT_PURPOSE_KEY) {
          purposeOption = getMapData(ele.value);
        } else if (ele.key === AppConstants.SYSTEM_CONFIG_CONSENT_CATEGORY_KEY) {
          categoryOption = getMapData(ele.value);
        } else if (ele.key === AppConstants.SYSTEM_CONFIG_CONSENT_STATUS_KEY) {
          statusOption = ele.allowedValues || [];
        }
      });

      setSystemFilterOption({
        purposeOption,
        categoryOption,
        statusOption
      });
      const {
        tableFilters = {},
        searchText = '',
        currentPage = AppConstants.DEFAULT_PAGE,
        size = type === 'listing'
          ? AppConstants.DEFAULT_PAGE_SIZE
          : AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE,
        sortName = AppConstants.DEFAULT_SORT_BY,
        sortType = AppConstants.DEFAULT_SORT_DESC
      } = globalFilters[`consent-list-${type}`] || {};
      if (isEmpty(getFilters(tableFilters))) {
        fetchConsentTemplates(
          searchText.trim(),
          currentPage,
          size,
          sortName,
          sortType,
          tableFilters
        );
      } else {
        setFormFilterData(tableFilters);
      }
    };
    if (type !== 'listing') {
      dispatch(
        setTableOptions({
          [`consent-list-${type}`]: {
            searchText: '',
            currentPage: AppConstants.DEFAULT_PAGE,
            size: AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE,
            sortName: '',
            sortType: '',
            tableFilters: {}
          }
        })
      );
    }
    if (type === 'migrationPreview') {
      setIsLoading(false);
      return;
    }

    init();

    dispatch(
      setURLPaths([
        {
          key: RouteConstants.ROUTE_CONSENTS,
          label: 'Consents'
        }
      ])
    );
  }, []);

  useDidMountEffect(() => {
    if (globalFilters[`consent-list-${type}`] && type !== 'migrationPreview') {
      const { searchText, size, sortName, sortType, tableFilters, currentPage } =
        globalFilters[`consent-list-${type}`];
      const page =
        getFilters(formFilterData) === getFilters(tableFilters)
          ? currentPage
          : AppConstants.DEFAULT_PAGE;
      fetchConsentTemplates(searchText.trim(), page, size, sortName, sortType, formFilterData);
    }
  }, [formFilterData]);

  const refreshTableData = (
    name: string,
    page: number,
    size: number,
    sortName: string = AppConstants.DEFAULT_SORT_BY,
    sortType: string = AppConstants.DEFAULT_SORT_DESC
  ) => {
    setTableValues(name, page, size, sortName, sortType);
  };

  const setTableValues = (
    name: string = '',
    page: number,
    size: number,
    sortName: string,
    sortType: string
  ) => {
    // if (type === 'migrationPreview') {
    //   setCurrentActivePage(page * size - size);
    // }
    fetchConsentTemplates(name, page, size, sortName, sortType);
  };

  const fetchConsentTemplates = async (
    name: string,
    page: number,
    size: number,
    sortName: string,
    sortType: string,
    filters?: filterConfigProps
  ) => {
    setIsLoading(true);
    const { tableFilters = {} } = globalFilters[`consent-list-${type}`] || {};
    dispatch(
      setTableOptions({
        [`consent-list-${type}`]: {
          searchText: name,
          currentPage: page,
          size: size,
          sortName: sortName,
          sortType: sortType,
          tableFilters: filters ?? tableFilters
        }
      })
    );
    const filter: string = getFilters(filters ?? tableFilters);
    const sorters: string[] = sortName ? [sortName, sortType] : [];
    let response = await listConsentTemplates(size, page, name, filter, sorters.join());
    if (response.status === HTTP_STATUS.HTTP_OK) {
      await fetchCreatedBy(response);
    } else {
      let error = getFailureMessage(response);
      setIsLoading(false);
      dispatch(setToastData({ isToastActive: true, toastMessage: error, toastType: 'error' }));
    }
  };

  const fetchCreatedBy = async (resposne: any) => {
    const { data = {}, status } = resposne;
    const { data: templateList = [], paging } = data;
    if (status === HTTP_STATUS.HTTP_OK) {
      if (templateList.length) {
        let newRow: any[] = [];
        templateList.forEach(async (ele: any, index: any) => {
          setIsLoading(true);
          newRow.push({
            ...ele,
            ...{
              purpose: ele.purpose,
              category: ele.category,
              action: ele.action,
              scope: ele.scope
            }
          });
          setRows(newRow);
          setPagingOpts(paging);
          setIsLoading(false);
        });
      } else {
        noDataAvailableMessage(setRows, () => {}, setPagingOpts, paging, t);
      }
    } else {
      let error = getFailureMessage(resposne);
      dispatch(setToastData({ isToastActive: true, toastMessage: error, toastType: 'error' }));
    }
    setIsLoading(false);
  };

  const getConsentTemplateDefaultSystemSettings = async () => {
    let systemSettings: Array<SystemSetting> = new Array<SystemSetting>();
    const response = await getSystemSettings(
      AppConstants.CATEGORY_ACCESS_CONTROL_CONSENT_TEMPLATE_DEFAULT_VALUES,
      '',
      AppConstants.DEFAULT_PAGE,
      AppConstants.MAXIMUM_PAGE_SIZE
    );

    const { status, data } = response;
    if (HTTP_STATUS.isSuccess(status)) {
      const { data: consentDefaults = [] } = data;
      systemSettings = [...consentDefaults];
    }
    setIsLoading(false);
    return systemSettings;
  };

  const getMapData = (value: Array<any> = []) => {
    return new Map<string, any>(
      value.map((item: any) => {
        const data = GetObjectFromData(item);
        return [data.display, data];
      })
    );
  };

  const getFilters = (formFilters: filterConfigProps) => {
    let filter: string[] = [];
    Object.entries(formFilters).forEach(([key, value]) => {
      if (!isEmpty(value)) {
        let codevalue = null;
        if (key === 'purpose-code') {
          let purposeCode: any = [];
          value.forEach((ele: any) => {
            const item = getCodeFromCodable(systemFilterOption.purposeOption.get(ele.key) || {});
            purposeCode.push(item);
          });
          codevalue = purposeCode.join(',');
        } else if (key === 'category-code') {
          let categoryCode: any = [];
          value.forEach((ele: any) => {
            const item = getCodeFromCodable(systemFilterOption.categoryOption.get(ele.key) || {});
            categoryCode.push(item);
          });
          codevalue = categoryCode.join(',');
        }
        filter.push(`${key}=${codevalue || value}`);
      }
    });
    return filter.join('&');
  };
  /** Migration section */
  useEffect(() => {
    setSelectedItems(manifestConsentsData);
  }, [manifestConsentsData]);

  useEffect(() => {
    if (type === 'migrationSelection') {
      setTableHeaders(consentHeaderBuilder());
    } else if (type === 'migrationPreview') {
      setRows(selectedItems);
      setIsLoading(false);
      setTableHeaders(consentHeaderBuilder());
    }
  }, [selectedItems]);

  useEffect(() => {
    if (type === 'migrationPreview') {
      const size = globalFilters?.[`consent-list-${type}`]?.size;
      if (size) {
        setCurrentActivePage(currentPage * size - size);
      }
    }
  }, [currentPage]);

  const saveSelection = () => {
    dispatch(setManifest({ key: 'consentTemplates', value: selectedItems }));
    toggleModal();
  };

  const removeSelection = (index: number) => {
    const row: any = rows[index + currentActivePage];
    if (row) {
      const { id } = row;
      dispatch(deleteManifestItem({ key: 'consentTemplates', id }));
    }
  };

  const isCheckboxSelected = (data: any) => {
    return isGlobalSelected(selectedItems, data);
  };

  const toggleCheckbox = (data: any) => {
    let arr = toggleGlobalCheckbox(selectedItems, data);
    setSelectedItems(arr);
  };

  const toggleOverWrite = (data: any) => {
    dispatch(
      setManifest({
        key: 'consentTemplates',
        value: toggleGlobalOverrideCheckbox(manifestConsentsData, data)
      })
    );
    return;
  };

  const consentHeaderBuilder = () => {
    let consentTemplateHeaders: Array<ITableHeader> = [
      {
        key: 'name',
        description: 'T_NAME',
        sortEnabled: type !== 'migrationPreview',
        width: type === 'listing' ? '20%' : '30%',
        sortingInformation: {
          order: ''
        }
      },
      {
        key: 'status',
        description: 'T_STATUS',
        sortEnabled: type !== 'migrationPreview',
        width: type === 'listing' ? '10%' : '15%',
        sortingInformation: {
          order: ''
        }
      },
      {
        key: 'purpose',
        description: 'T_PURPOSE',
        sortEnabled: false,
        width: type === 'listing' ? '20%' : '25%',
        nastedData: true,
        render: (data: any) => {
          return getPurpose(data).join(', ');
        },
        sortingInformation: {
          order: ''
        }
      },
      {
        key: 'type',
        description: 'T_TYPE',
        sortEnabled: type !== 'migrationPreview',
        width: type === 'listing' ? '10%' : '15%',
        sortingInformation: {
          order: ''
        }
      },
      {
        key: 'version',
        description: 'T_VERSION',
        sortEnabled: type !== 'migrationPreview',
        width: type === 'listing' ? '10%' : '15%',
        sortingInformation: {
          order: ''
        }
      }
    ];

    switch (type) {
      case 'listing':
        consentTemplateHeaders = consentTemplateHeaders.concat([
          {
            key: 'action',
            description: 'T_ACTION',
            sortEnabled: false,
            nastedData: true,
            render: (data: any) => {
              return getCodableValueArray(data.action || []).join(', ');
            },
            width: '15%'
          },
          {
            key: 'category',
            description: 'T_CATEGORY',
            sortEnabled: false,
            nastedData: true,
            render: (data: any) => {
              return getCodableValueArray(data.category || []).join(', ');
            },
            width: '15%'
          },
          {
            key: 'updatedOn',
            description: 'T_LAST_MODIFIED_ON',
            sortEnabled: true,
            render: (data: any) => {
              return data.updatedOn
                ? moment(data.updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT)
                : '';
            },
            sortingInformation: {
              order: ''
            },
            width: '15%'
          }
        ]);

        break;

      case 'migrationSelection':
        consentTemplateHeaders.unshift({
          key: 'checked',
          description: '',
          sortEnabled: false,
          width: '10%',
          sortingInformation: {
            order: ''
          },
          nastedData: true,
          render: (data: any, index: any) => {
            return (
              <>
                <Checkbox
                  className="mx-auto"
                  height="20px"
                  width="20px"
                  checked={isCheckboxSelected(data)}
                  onChange={() => {
                    toggleCheckbox(data);
                  }}
                  testID="addModuleCheckbox"
                />
              </>
            );
          }
        });
        break;
      case 'migrationPreview':
        consentTemplateHeaders.push(
          {
            key: 'overWrite',
            description: 'T_CONFIG_KEY_ALLOW_OVERRIDE',
            sortEnabled: false,
            width: '10%',
            sortingInformation: {
              order: ''
            },
            nastedData: true,
            render: (data: any, index: any) => {
              return (
                <>
                  <Checkbox
                    className="mx-auto"
                    height="20px"
                    width="20px"
                    onChange={() => {
                      toggleOverWrite(data);
                    }}
                    checked={data?.overWrite}
                    testID="allowOverWriteCheckbox"
                  />
                </>
              );
            }
          },
          {
            key: 'buttons',
            description: t('T_ACTIONS'),
            sortEnabled: false,
            width: '10%',
            sortingInformation: {
              order: ''
            },
            styles: {
              justifyContent: 'center'
            }
          }
        );
        break;
    }

    return consentTemplateHeaders;
  };

  const [tableHeaders, setTableHeaders] = useState<Array<ITableHeader>>(consentHeaderBuilder());

  return (
    <>
      <LoaderContainer isLoading={isLoading}>
        {type === 'listing' && (
          <MenuButtonsPortal>
            <BiButton
              className={'flex flex-row bg-primary text-white'}
              type="button"
              disabled={false}
              onClick={() => {
                navigate(RouteConstants.ROUTE_NEW_CONSENTS);
              }}
            >
              <img src={AddCircle} className="mr-2 w-6 text-center"></img>
              {t('T_NEW_CONSENT')}
            </BiButton>
          </MenuButtonsPortal>
        )}

        <CustomersTableContainer style={{ height: 'inherit' }}>
          {type !== 'migrationPreview' && (
            <ConsentListFilter
              filterConfig={formFilterData}
              setFilterConfig={setFormFilterData}
              systemFilterOption={systemFilterOption}
            />
          )}
          <TemplateTable
            tableData={
              type === 'migrationPreview'
                ? rows.slice(
                    currentActivePage,
                    currentActivePage + AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE
                  )
                : rows
            }
            size={
              type === 'migrationPreview'
                ? AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE
                : globalFilters?.[`consent-list-${type}`]?.size
            }
            templateSelector={consentTemplateSelector}
            onRefreshTableData={refreshTableData}
            currentPage={
              type === 'migrationPreview'
                ? currentPage
                : globalFilters?.[`consent-list-${type}`]?.currentPage.toString()
            }
            setCurrentPage={(page: any) => {
              setCurrentPage(parseInt(page));
            }}
            tableHeaders={tableHeaders}
            setTableHeaders={setTableHeaders}
            deleteOnClick={removeSelection}
            searchPlaceholder={t('T_SEARCH_NAME')}
            pagingData={pagingOpts}
            containerClass="mt-0"
            showDeleteButton={true}
            theme={useTheme().theme}
            isSearchable={false}
            hasExternalCustomFilter={true}
            globalFilters={globalFilters}
            tablePage={`consent-list-${type}`}
            isLocalPaginator={type === 'migrationPreview'}
            isPaginated={type !== 'migrationPreview'}
            disablePageSize={type !== 'listing'}
            type={type}
            totalDataLength={rows.length}
          />
          {type === 'migrationSelection' && (
            <div className="w-full">
              <ActionButtons
                primaryButtonLabel={t('T_SAVE')}
                primaryButtonAction={saveSelection}
                secondaryButtonLabel={t('T_CANCEL')}
                secondaryButtonAction={toggleModal}
                primaryButtonWidth="full"
                primaryButtonClass={'px-4'}
              />
            </div>
          )}
        </CustomersTableContainer>
      </LoaderContainer>
    </>
  );
};

export default ConsentTemplateList;
